// @ts-nocheck
/**
 * Distribution Component - Performance Optimized
 * 
 * Optimizations:
 * - Memoized calculations and event handlers
 * - Reduced unnecessary re-renders
 * - Cleaner state management
 * - Optimized array operations
 * - Better separation of concerns
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import constant from "@utils/constant";
import Sliders from "@widgets/Sliders";

// Constants to prevent recreation
const EMPTY_ARRAY = [];

// Utility functions
const formatPresetName = (presetName) => {
  const presetMap = {
    "I Only Love Octopi": "I Only ❤️ Octopi",
    "Art Lover": "🎨 Lover",
    "Cat Lover": "🐱 Lover"
  };
  return presetMap[presetName] || presetName;
};

const calculateSellerDistribution = (beneficiary, artist, sponsor, reddevcut) => {
  return 100 - (beneficiary + (artist + reddevcut) + (sponsor + reddevcut));
};

const Distribution = ({
  distribution = [],
  sliders = [],
  setPieData,
  pieData = [],
  setUserSelection,
  preset = '',
  setPreset,
  setDefaultSlider,
  showPreset = true,
  setShowPreset,
  selectedItems = {},
  reddevcut = 0
}) => {
  const [slider, setSlider] = useState([]);

  // Memoized CSS classes
  const sliderClass = useMemo(() => 
    !showPreset ? "d-block" : "d-none", 
    [showPreset]
  );
  
  const presetClass = useMemo(() => 
    showPreset ? "d-block" : "d-none", 
    [showPreset]
  );

  // Memoized title
  const distTitle = useMemo(() => 
    showPreset ? constant.PICK_DISTRO : constant.DISTRIBUTION,
    [showPreset]
  );

  // Effect to handle preset visibility based on selection
  useEffect(() => {
    if (selectedItems?.distribution?.presetName === constant.MY_DISTRO) {
      setShowPreset(false);
    }
  }, [selectedItems?.distribution?.presetName, setShowPreset]);

  // Effect to initialize sliders and pie data
  useEffect(() => {
    if (sliders.length === 0) return;

    const defaultValues = sliders.map(slider => slider.defaultValue);
    setSlider(defaultValues);
    setPieData(defaultValues);
    
    if (!showPreset) {
      setPreset(constant.MY_DISTRO);
    }
  }, [showPreset, setPreset, sliders, setPieData]);

  // Memoized distribution calculation
  const calculateDistribution = useCallback((distIndex) => {
    // Type guard to ensure distribution is an array with items
    if (!Array.isArray(distribution) || distribution.length === 0 || distIndex >= distribution.length) {
      return null;
    }

    const dist = distribution[distIndex];
    // Additional safety check
    if (!dist || typeof dist !== 'object') {
      return null;
    }

    const beneficiary = (dist.beneficiaryDistro || 0) / 100;
    const artist = (dist.artistDistro || 0) / 100;
    const sponsor = (dist.sponsorDistro || 0) / 100;
    const seller = calculateSellerDistribution((beneficiary * 100, artist * 100, sponsor * 100, reddevcut) / 100);

    return [beneficiary, artist, sponsor, seller];
  }, [distribution, reddevcut]);

  // Optimized preset change handler
  const changePreset = useCallback((index) => {
    const distributionData = calculateDistribution(index);
    if (!distributionData || !distribution[index]) return;

    const [beneficiary, artist, sponsor, seller] = distributionData;
    const dist = distribution[index];

    // Update pie data
    setPieData([...distributionData]);
    setPreset(dist.presetName || '');

    // Update user selection
    setUserSelection({
      distribution: {
        presetName: dist.presetName || '',
        beneficiaryDistro: beneficiary * 100,
        artistDistro: artist * 100,
        sponsorDistro: sponsor * 100,
        sellerDistro: (seller || 0) * 100,
        category: "distribution",
        actualName: dist.presetName || '',
      },
    });

    // Update slider defaults
    const updatedSliders = sliders.map((sl, i) => ({
      ...sl,
      defaultValue: distributionData[i] || 0
    }));
    setDefaultSlider(updatedSliders);
  }, [calculateDistribution, distribution, setPieData, setPreset, setUserSelection, sliders, setDefaultSlider]);

  // Optimized slider change handler
  const handleSliderChange = useCallback((newSlider) => {
    if(showPreset) return;
    setSlider(newSlider);
    
    // Create new pie data array
    const newPieData = [...newSlider];
    setPieData(newPieData);
    setPreset(constant.MY_DISTRO);

    // Update user selection
    setUserSelection({
      distribution: {
        presetName: constant.MY_DISTRO,
        beneficiaryDistro: newSlider[0] * 100,
        artistDistro: newSlider[1] * 100,
        sponsorDistro: newSlider[2] * 100,
        sellerDistro: (newSlider[3] || 0) * 100,
        category: "distribution",
      },
    });

    // Update slider defaults
    const updatedSliders = sliders.map((sl, i) => ({
      ...sl,
      defaultValue: newSlider[i] || 0
    }));
    setDefaultSlider(updatedSliders);
  }, [setPieData, setPreset, setUserSelection, sliders, setDefaultSlider]);

  // Effect to set default preset
  useEffect(() => {
    if (preset.length === 0 && distribution.length > 0) {
      changePreset(0);
    }
  }, [distribution.length, preset.length, changePreset]);

  // Memoized preset selection checker
  const isPresetSelected = useCallback((presetName) => {
    return presetName === (selectedItems?.distribution?.actualName || '');
  }, [selectedItems]);

  // Memoized preset items
  const presetItems = useMemo(() => {
    if (!Array.isArray(distribution) || distribution.length === 0) {
      return [];
    }
    
    return distribution.map((dist, index) => {
      if (!dist || typeof dist !== 'object') {
        return null;
      }
      
      return (
        <div
          key={`${dist.presetName || 'preset'}-${index}`}
          className={`dist-preset__box ${
            isPresetSelected(dist.presetName || '') ? "dist-preset__box--selected" : ""
          }`}
          onClick={() => changePreset(index)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              changePreset(index);
            }
          }}
          aria-label={`Select ${dist.presetName || 'distribution'} distribution preset`}
        >
          <span className="dist-preset__text">
            {formatPresetName(dist.presetName || '')}
          </span>
        </div>
      );
    }).filter(Boolean);
  }, [distribution, isPresetSelected, changePreset]);

  return (
    <>
      <h3 className="mint__step-title">{distTitle}</h3>
      <div className="mint__dist">
        <span className="mint__dist-desc">
          Pick your Alter-Ego for funds distribution when this NFT is minted
          and each time it is sold:
        </span>
        <div className="mint__dist-presets">
          <div className="preset-wrapper">
            <div className={presetClass}>
              <div className="mint__dist-presets--predefined">
                {presetItems}
              </div>
            </div>
            <div className={sliderClass}>
              <div className="slider-box">
                <Sliders
                  sliders={sliders}
                  slider={slider}
                  handleChange={handleSliderChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Distribution;
